// frontend/src/components/Layout/CustomerAccountSidebar.tsx
// Mini sidebar navigation for customer account pages - like e-commerce "My Account" sections

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { customerAccountNavigation } from './configs/customerAccountConfig';

interface CustomerAccountSidebarProps {
  className?: string;
}

const CustomerAccountSidebar: React.FC<CustomerAccountSidebarProps> = ({ 
  className = '' 
}) => {
  const location = useLocation();
  const { user } = useAuth();
  
  const currentPath = location.pathname;

  // Check if current path matches navigation item
  const isActivePath = (href: string) => {
    // Exact match for base account page
    if (href === '/account' && currentPath === '/account') {
      return true;
    }
    // For sub-pages, check if current path starts with the href
    if (href !== '/account' && currentPath.startsWith(href)) {
      return true;
    }
    return false;
  };

  // Get user display info
  const getUserDisplayName = () => {
    if (!user) return 'Account';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return user.username || user.email || 'Account';
  };

  const getUserInitials = () => {
    if (!user) return 'A';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) return firstName.charAt(0).toUpperCase();
    if (user.username) return user.username.charAt(0).toUpperCase();
    return 'A';
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 sticky top-6 ${className}`}>
      
      {/* Sidebar Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          {/* User Avatar */}
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            {user?.profile_image ? (
              <img
                src={user.profile_image}
                alt={getUserDisplayName()}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <span className="text-blue-600 font-semibold text-sm">
                {getUserInitials()}
              </span>
            )}
          </div>
          
          {/* User Info */}
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-900 text-sm truncate">
              {getUserDisplayName()}
            </h2>
            <p className="text-xs text-gray-500 truncate">
              {user?.email}
            </p>
          </div>
        </div>
      </div>
      
      {/* Navigation Items */}
      <nav className="p-4">
        <div className="space-y-1">
          {customerAccountNavigation.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.href);
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  group flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                  ${isActive 
                    ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm' 
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                title={item.description}
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    className={`h-4 w-4 flex-shrink-0 transition-colors ${
                      isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                    }`} 
                  />
                  <span className="truncate">{item.name}</span>
                </div>
                
                {/* Badge */}
                {item.badge && (
                  <span className={`
                    inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                    ${isActive 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-red-100 text-red-800'
                    }
                  `}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Quick Actions Footer */}
      <div className="border-t border-gray-200 p-4">
        <div className="space-y-2">
          
          {/* Primary CTA */}
          <Link
            to="/services"
            className="w-full flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span>Book a Service</span>
          </Link>
          
          {/* Secondary Action */}
          <Link
            to="/account/favorites"
            className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span>My Favorites</span>
          </Link>
        </div>
      </div>

      {/* Support Section */}
      <div className="border-t border-gray-200 p-4">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-2">Need help?</p>
          <a
            href="/contact"
            className="text-blue-600 hover:text-blue-800 text-xs font-medium"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default CustomerAccountSidebar;